import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';

export interface QuotaImportError {
  line: number;
  column?: string;
  message: string;
}

/** `sheetSector` / `sheetBrand` appear only when the grid spells the pair differently. */
export interface QuotaImportEntry {
  sector: string;
  brand: string;
  limit: number;
  id?: string;
  from?: number;
  sheetSector?: string;
  sheetBrand?: string;
}

export interface QuotaImportDiff {
  /** Rules already on the chosen scope. Zero means the scope is empty — usually a wrong pick. */
  existingCount: number;
  /** Values no existing quota on the desk uses. Written as spelled, surfaced to catch a typo. */
  unverified: { sectors: string[]; brands: string[] };
  /** Pairs already carrying several rules. The first is updated, the others left untouched. */
  duplicates: string[];
  toCreate: QuotaImportEntry[];
  toUpdate: QuotaImportEntry[];
  toReactivate: QuotaImportEntry[];
  toArchive: QuotaImportEntry[];
  unchanged: QuotaImportEntry[];
  errors: QuotaImportError[];
}

export interface QuotaImportScope {
  serviceDeskId: number;
  requestTypeId: number;
  disableMissing?: boolean;
}

const body = (file: File, scope: QuotaImportScope): FormData => {
  const form = new FormData();
  form.append('file', file);
  form.append('serviceDeskId', String(scope.serviceDeskId));
  form.append('requestTypeId', String(scope.requestTypeId));
  form.append('disableMissing', String(Boolean(scope.disableMissing)));
  return form;
};

export const quotasApi = {
  validateImport: async (file: File, scope: QuotaImportScope): Promise<QuotaImportDiff> => {
    const res = await fetchWithAuth('admin/quotas/import/validate', { method: 'POST', body: body(file, scope) });
    return unwrap<QuotaImportDiff>(res, 'Failed to read the quota CSV');
  },

  applyImport: async (file: File, scope: QuotaImportScope): Promise<{ diff: QuotaImportDiff; applied: boolean }> => {
    const res = await fetchWithAuth('admin/quotas/import/apply', { method: 'POST', body: body(file, scope) });
    return unwrap<{ diff: QuotaImportDiff; applied: boolean }>(res, 'Failed to import the quota CSV');
  },
};
